/**
 * Notebook file upload — calls /fs/dashboardUpload/largeFilePrepare
 * (NOT the material upload endpoint) to get OSS/COS credentials,
 * then uploads directly from the browser.
 *
 * Keep this separate from the material library upload flow.
 */
import apiClient from './client'
import DASHBOARD_API from '@/api/DASHBOARD_API'
import { getAuthApiUrl } from '@/lib/config'
import { DEFAULT_LOGIN_ROUTE } from '@/lib/constants'
import { redirectToAppRoute } from '@/lib/navigation'

// ── Types ───────────────────────────────────────────────────────────────────

interface DashboardEnvelope<T> {
  code?: string | number
  data?: T
  msg?: string
  message?: string
}

export interface NotebookUploadPrepareResult {
  provider?: string
  uploadMode?: string
  region?: string
  accessKeyId?: string
  accessKeySecret?: string
  stsToken?: string
  expiration?: string
  bucket?: string
  dir?: string
  filename?: string
  domain?: string
  cdnDomain?: string
  uploadUrl?: string
  uploadMethod?: string
}

export interface NotebookUploadedFile {
  fileUrl: string
  url: string
  name: string
  size: number
}

export interface NotebookUploadProgress {
  file: File
  progress: number // 0-100
  status: 'pending' | 'uploading' | 'success' | 'error'
  error?: string
  result?: NotebookUploadedFile
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function unwrapDashboardResponse<T>(body: DashboardEnvelope<T> | T): T {
  if (isRecord(body) && 'code' in body) {
    const envelope = body as DashboardEnvelope<T>
    const code = Number(envelope.code)
    if (Number.isFinite(code) && code >= 200 && code < 300) {
      return envelope.data as T
    }
    throw new Error(envelope.msg || envelope.message || '请求失败')
  }
  if (isRecord(body) && 'data' in body) {
    return (body as DashboardEnvelope<T>).data as T
  }
  return body as T
}

async function dashboardPost<T>(url: string, data?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.post<DashboardEnvelope<T> | T>(url, data, {
    baseURL: await getAuthApiUrl(),
    withCredentials: true,
    headers: {
      Source: DASHBOARD_API.SOURCE,
      'Source-App': DASHBOARD_API.SOURCE_APP,
    },
  })
  try {
    return unwrapDashboardResponse<T>(response.data)
  } catch (error) {
    if (error instanceof Error && error.message === '未登录') {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('auth-storage')
        redirectToAppRoute(DEFAULT_LOGIN_ROUTE)
      }
    }
    throw error
  }
}

function trimSlash(value = '') {
  return value.replace(/\/+$/, '')
}

function buildObjectKey(prepare: NotebookUploadPrepareResult = {}) {
  const dir = trimSlash(prepare.dir || '')
  const filename = prepare.filename || ''
  return `${dir}/${filename}`.replace(/^\/+/, '')
}

function buildDirectFileUrl(prepare: NotebookUploadPrepareResult = {}, objectKey = buildObjectKey(prepare)) {
  let baseUrl = trimSlash(prepare.cdnDomain || prepare.domain || '')
  if (baseUrl && !/^https?:\/\//i.test(baseUrl) && !baseUrl.startsWith('//')) {
    baseUrl = `https://${baseUrl}`
  }
  return baseUrl ? `${baseUrl}/${objectKey}` : objectKey
}

function parseExpiration(value?: string) {
  const seconds = value ? Math.floor(new Date(value).getTime() / 1000) : 0
  if (Number.isFinite(seconds) && seconds > 0) return seconds
  return Math.floor(Date.now() / 1000) + 900
}

// ── OSS / COS upload ───────────────────────────────────────────────────────

async function uploadByAliyun(file: File, prepare: NotebookUploadPrepareResult) {
  const objectKey = buildObjectKey(prepare)
  const OSS = (await import('ali-oss')).default
  const client = new OSS({
    accessKeyId: prepare.accessKeyId,
    accessKeySecret: prepare.accessKeySecret,
    stsToken: prepare.stsToken,
    region: prepare.region,
    bucket: prepare.bucket,
    secure: true,
    timeout: 600000,
  })
  await client.put(objectKey, file)
  return { fileUrl: objectKey, url: buildDirectFileUrl(prepare, objectKey) }
}

async function uploadByCos(file: File, prepare: NotebookUploadPrepareResult) {
  const objectKey = buildObjectKey(prepare)
  const COS = (await import('cos-js-sdk-v5')).default
  const cos = new COS({
    getAuthorization: (_options: unknown, callback: (cred: Record<string, unknown>) => void) => {
      callback({
        TmpSecretId: prepare.accessKeyId || '',
        TmpSecretKey: prepare.accessKeySecret || '',
        SecurityToken: prepare.stsToken || '',
        StartTime: Math.floor(Date.now() / 1000) - 5,
        ExpiredTime: parseExpiration(prepare.expiration),
      })
    },
  })
  await new Promise<void>((resolve, reject) => {
    cos.putObject({
      Bucket: prepare.bucket || '',
      Region: prepare.region || '',
      Key: objectKey,
      Body: file,
      ContentType: file.type || 'application/octet-stream',
    }, (err: unknown) => {
      if (err) { reject(err); return }
      resolve()
    })
  })
  return { fileUrl: objectKey, url: buildDirectFileUrl(prepare, objectKey) }
}

async function uploadDirectFile(
  file: File,
  prepare: NotebookUploadPrepareResult,
): Promise<{ fileUrl: string; url: string }> {
  if (!prepare.filename || !prepare.dir) {
    throw new Error('上传凭证缺少文件路径')
  }

  if (prepare.uploadMode === 'presigned_url') {
    if (!prepare.uploadUrl) throw new Error('上传凭证缺少直传地址')
    const response = await fetch(prepare.uploadUrl, {
      method: prepare.uploadMethod || 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    })
    if (!response.ok) throw new Error(`上传失败：${response.status}`)
    const objectKey = buildObjectKey(prepare)
    return { fileUrl: objectKey, url: buildDirectFileUrl(prepare, objectKey) }
  }

  if (prepare.provider === 'cos' || prepare.provider === 'tencent') {
    return uploadByCos(file, prepare)
  }
  return uploadByAliyun(file, prepare)
}

// ── Public API ─────────────────────────────────────────────────────────────

export async function prepareNotebookUpload(
  file: File,
  dir: string,
): Promise<NotebookUploadPrepareResult> {
  // Detect file type for the path parameter
  const ext = file.name.split('.').pop()?.toLowerCase() || 'file'
  const fileType = (() => {
    if (['jpg','jpeg','png','gif','bmp','svg','ico','tiff','tif','webp'].includes(ext)) return 'img'
    if (['mp4','avi','mov','wmv','flv','mkv','webm','m4v','mpg','mpeg','3gp'].includes(ext)) return 'video'
    if (['mp3','wav','aac','ogg','flac','wma','m4a','ape','aiff'].includes(ext)) return 'audio'
    return 'file'
  })()
  return dashboardPost<NotebookUploadPrepareResult>(
    DASHBOARD_API.materials.largeFilePrepare(fileType),
    { dir, fileName: file.name },
  )
}

export async function uploadNotebookFile(
  file: File,
  dir: string,
  onProgress?: (progress: number) => void,
): Promise<NotebookUploadedFile> {
  onProgress?.(5)

  // Step 1: Get upload credentials from dashboard API
  const prepare = await prepareNotebookUpload(file, dir)
  onProgress?.(20)

  // Step 2: Upload directly to OSS/COS
  const uploaded = await uploadDirectFile(file, {
    ...prepare,
    dir: prepare.dir || '',
    filename: prepare.filename || file.name,
  })
  onProgress?.(100)

  return {
    fileUrl: uploaded.fileUrl,
    url: uploaded.url,
    name: file.name,
    size: file.size,
  }
}

/**
 * Upload multiple files in sequence, reporting progress for each.
 */
export async function uploadNotebookFiles(
  files: File[],
  dir: string,
  onFileProgress?: (index: number, progress: number) => void,
): Promise<NotebookUploadedFile[]> {
  const results: NotebookUploadedFile[] = []
  for (let i = 0; i < files.length; i++) {
    const result = await uploadNotebookFile(files[i], dir, (p) => onFileProgress?.(i, p))
    results.push(result)
  }
  return results
}
